import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { GoldButton, Section } from "@/components/site/Ui";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Check, X, Lightbulb, ShieldCheck } from "lucide-react";
import PayPalButton from "@/components/site/PayPalButton";
import { useNavigate } from "react-router-dom";

const PLAN_FAQS = [
  { q: "How is my plan personalised?", a: "You tell us your platform and niche in a short questionnaire after buying. Every idea, title and tag is made for you." },
  { q: "Do you design my thumbnails?", a: "We give you a clear thumbnail design guide (what it should look like and why). You create it — it's quick with the guide." },
  { q: "What's the script add-on?", a: "For a small extra fee we write a full word-for-word script for each video, so you never freeze on camera." },
  { q: "How soon do I get it?", a: "After your questionnaire, we deliver within 3-5 working days by secure download link and email." },
];

const SAMPLE = {
  title: "3 Cheap Phone Accessories That Instantly Improve Your Videos",
  hook: "Filmed at home in under 10 minutes.",
  bullets: ["SEO title ready to paste", "Step-by-step phone filming guide", "Description + tags included", "Thumbnail guide: bold text + your face, top-left"],
};

export default function ContentPlans() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [pricing, setPricing] = useState({});
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", add_script: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/services-pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const startBuy = (p) => { setSelected(p); setForm({ name: "", email: "", add_script: false }); setOpen(true); };

  const checkout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected.lookup_key,
        add_script: form.add_script,
        customer_email: form.email,
        customer_name: form.name,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not start checkout.");
      setLoading(false);
    }
  };

  const addon = pricing.script_addon_price || 10;
  const total = selected ? (selected.price + (form.add_script ? addon : 0)).toFixed(2) : 0;

  const allFeatures = [
    "Clickable, watchable video ideas", "Step-by-step phone filming guide", "SEO-optimised titles",
    "Ready-to-use descriptions", "Tags & keywords", "Thumbnail design guide", "Full script add-on available",
  ];

  return (
    <>
      <div className="relative grain overflow-hidden">
        <div className="absolute -top-40 right-0 h-[400px] w-[400px] rounded-full bg-[#D4AF37]/10 blur-[120px] -z-10" />
        <Section className="pb-8">
          <Reveal>
            <Eyebrow>Content Plans</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05]">Never wonder <span className="gold-text">what to post</span> again.</h1>
            <p className="mt-5 text-white/70 max-w-2xl text-lg">A personalised list of clickable video ideas for your exact platform and niche — with everything you need to film each one on your phone.</p>
          </Reveal>
        </Section>
      </div>

      {/* Packages */}
      <Section className="pt-4">
        <div className="grid md:grid-cols-2 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <div className={`relative rounded-3xl p-8 h-full flex flex-col ${p.best_value ? "border-2 border-[#D4AF37] gold-glow" : "card-surface"}`}>
                {p.best_value && <span className="absolute -top-3 left-8 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F0D77B] px-3 py-1 text-[11px] font-bold text-[#0A0A0A]">BEST VALUE</span>}
                <h3 className="text-xl font-display pr-4">{p.name}</h3>
                <div className="mt-3 flex items-end gap-1.5">
                  <span className="text-5xl font-display gold-text">£{p.price}</span>
                  <span className="text-white/40 text-sm mb-2">/ {p.videos} videos</span>
                </div>
                <p className="mt-4 text-sm text-white/60 leading-relaxed">{p.description}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex gap-2.5 text-sm text-white/75"><Check size={17} className="text-[#D4AF37] shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <GoldButton onClick={() => startBuy(p)} data-testid={`buy-${p.lookup_key}`}
                  variant={p.best_value ? "solid" : "outline"} className="mt-7 w-full">Buy Now</GoldButton>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Comparison table */}
      <div className="relative bg-[#0d0d0d] border-y border-white/5">
        <Section>
          <Reveal><Eyebrow>Compare</Eyebrow><h2 className="mt-4 font-display text-2xl sm:text-3xl">What's included</h2></Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 overflow-x-auto rounded-2xl card-surface">
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-white/60 font-normal">Feature</th>
                    {products.map((p) => <th key={p.id} className="p-4 text-center font-display text-[#D4AF37]">{p.videos} videos</th>)}
                  </tr>
                </thead>
                <tbody>
                  {allFeatures.map((f, idx) => (
                    <tr key={f} className="border-b border-white/5">
                      <td className="p-4 text-white/75">{f}</td>
                      {products.map((p) => (
                        <td key={p.id} className="p-4 text-center">
                          <Check size={18} className="inline text-[#D4AF37]" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </Section>
      </div>

      {/* Sample */}
      <Section>
        <Reveal><Eyebrow>See a sample</Eyebrow><h2 className="mt-4 font-display text-2xl sm:text-3xl">An example idea card</h2></Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 max-w-2xl rounded-2xl card-surface p-7 gold-glow">
            <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest"><Lightbulb size={16} /> Video idea</div>
            <h3 className="mt-3 text-xl font-display">{SAMPLE.title}</h3>
            <p className="mt-2 text-white/50 text-sm italic">{SAMPLE.hook}</p>
            <ul className="mt-5 space-y-2">
              {SAMPLE.bullets.map((b) => <li key={b} className="flex gap-2.5 text-sm text-white/75"><Check size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />{b}</li>)}
            </ul>
          </div>
        </Reveal>
      </Section>

      {/* FAQ */}
      <div className="relative bg-[#0d0d0d] border-y border-white/5">
        <Section className="max-w-3xl">
          <Reveal><Eyebrow>Plan questions</Eyebrow><h2 className="mt-4 font-display text-2xl sm:text-3xl">Good to know</h2></Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-8">
              {PLAN_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`i${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left hover:text-[#D4AF37] hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/60 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </Section>
      </div>

      {/* Buy dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md" data-testid="checkout-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{selected?.name}</DialogTitle></DialogHeader>
          <form onSubmit={checkout} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Your name *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="checkout-name"
                className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="checkout-email"
                className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37]" />
            </div>
            <label className="flex items-start gap-3 rounded-lg border border-white/10 p-3 cursor-pointer hover:border-[#D4AF37]/40">
              <input type="checkbox" checked={form.add_script} onChange={(e) => setForm({ ...form, add_script: e.target.checked })}
                data-testid="checkout-addon" className="mt-1 accent-[#D4AF37]" />
              <span className="text-sm text-white/80">Add full video scripts <span className="text-[#D4AF37]">+£{addon}</span><br />
                <span className="text-white/45 text-xs">Word-for-word scripts for every video.</span></span>
            </label>
            <div className="flex items-center justify-between border-t border-white/10 pt-4">
              <span className="text-white/60 text-sm">Total</span>
              <span className="text-2xl font-display gold-text">£{total}</span>
            </div>
            <GoldButton type="submit" disabled={loading} data-testid="checkout-submit" className="w-full">
              {loading ? "Redirecting..." : "Pay by card (Stripe)"}
            </GoldButton>
            {selected && (
              <PayPalButton
                getPayload={() => (form.name && form.email ? { lookup_key: selected.lookup_key, add_script: form.add_script, customer_email: form.email, customer_name: form.name } : null)}
                onSuccess={(res) => { setOpen(false); navigate(`/questionnaire/${res.questionnaire_token}`); toast.success("Payment successful!"); }}
              />
            )}
            <p className="flex items-center justify-center gap-1.5 text-xs text-white/40"><ShieldCheck size={14} /> Secured payments · GBP</p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
